// Live's Properties tab, its caption theme editor (#264 part 2, spec docs/specs/editor/captions.md):
// the tabs Captions, Paragraph, Active and Emphasize, every control in live's order, words and
// ranges. Every change puts the look on every caption (live has no per-caption edit); a Paragraph
// change and "Auto size fit lines" re-cut every caption from its own words, Position and the
// box's numbers move the captions' boxes. "Save style as preset" (part 3) keeps the whole look for
// the member who saved it, named as live names it.
import { Button, NumberInput, Select, Switch, Text } from "@mantine/core";
import { Bookmark, Infinity as Loop, Strikethrough, Underline } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import {
  getListCaptionPresetsQueryKey,
  useCreateCaptionPreset,
  useListCaptionPresets,
} from "../../../api/generated/endpoints/editor/editor";
import type { CaptionAnimationPreset, CaptionPreset, CaptionScope, FontFamily } from "../../../api/generated/model";
import {
  boxHeightMax,
  boxWidthMax,
  boxXMax,
  boxXMin,
  boxYMax,
  boxYMin,
  captionParagraphCharacterCountMax,
  captionParagraphPauseSMax,
  captionParagraphPauseSMin,
  captionParagraphSplitSMax,
  captionParagraphSplitSMin,
  captionParagraphWordsPerLineMax,
  captionPresetCreateNameMax,
  captionStyleLetterSpacingMax,
  captionStyleLetterSpacingMin,
  captionStyleLineHeightMax,
  captionStyleLineHeightMin,
  captionStyleSizeMax,
  captionStyleSizeMin,
} from "../../../api/generated/zod";
import { toUserMessage } from "../../../platform/messages";
import { liveFrame } from "../engine/project";
import { FONT_FAMILIES, stylesOf } from "../fonts";
import {
  ALIGNS,
  BackgroundFields,
  CASES,
  ColorField,
  ExtraSection,
  LookNumber,
  LookRow,
  ParamSection,
  ShadowFields,
  StrokeFields,
  Toggle,
} from "../params";
import type { AspectRatio } from "../project";
import { LINE_PITCH } from "../text-style";
import { newId, spokenWords, type CaptionClip, type Timeline, type TimelineEdit } from "../timeline";
import { CAPTION_ANIMATIONS, captionAnimationOf } from "./caption-animations";
import { toWireCaptions } from "../wire";
import { DEFAULT_CAPTIONS, type CaptionSettings, type CaptionStyle, type CaptionTag } from "./caption-style";
import { presetLook, recutRefusal } from "./PresetsTab";
import { captionCut, cutWith } from "./recut";
import { MAX_CAPTIONS } from "./words";
import { placedBox, type Frequency, type Paragraph, type Placement } from "./segment";
import type { Said } from "./Transcript";
import type { CaptionBox } from "./words";
import classes from "./captions.module.css";

export interface ThemeEditorProps {
  teamId: string;
  timeline: Timeline;
  aspectRatio: AspectRatio;
  readOnly: boolean;
  /** The chosen caption, whose box the size and position fields show; else the first. */
  chosenCaption: string | null;
  onEdit: (edit: TimelineEdit) => void;
  onSay: (said: Said | null) => void;
}

type ThemeTab = "captions" | "paragraph" | "active" | "emphasize";
const TABS: readonly { id: ThemeTab; label: string }[] = [
  { id: "captions", label: "Captions" },
  { id: "paragraph", label: "Paragraph" },
  { id: "active", label: "Active" },
  { id: "emphasize", label: "Emphasize" },
];

/** What the editor's "+" sets, live's: a stroke, a shadow (its alpha 0.85), a background. */
const ADDED_STROKE = { color: "#000000", width: 16.7 }; // raw-color: live's default
const ADDED_SHADOW = { color: "#000000", alpha: 0.85, blur: 6, offsetX: 3, offsetY: 3 }; // raw-color: live's default
const ADDED_COLOR = "#FFFFFF"; // raw-color: live's default
const ADDED_ACTIVE_COLOR = "#FF5446"; // raw-color: live's default
const ADDED_UPCOMING_COLOR = "#FFFFFF"; // raw-color: live's default
const ADDED_ACTIVE_BACKGROUND = "#000000"; // raw-color: live's default
const ADDED_TAG: Readonly<Record<"positive" | "negative", CaptionTag>> = {
  positive: { color: "#F2ED6B", font: null, weight: 700, italic: false, sizeRatio: 1, underline: false, difference: false, animation: null }, // raw-color: live's default
  negative: { color: "#FF3B30", font: null, weight: 700, italic: false, sizeRatio: 1, underline: false, difference: false, animation: null }, // raw-color: live's default
};
/** Live's "Supersize (120%)", as its words say (spec decision 28: live's set 150 %). */
const SUPERSIZE = 1.2;

/** Live's nine places, as its Position list names them. */
const POSITIONS: readonly { label: string; placement: Placement }[] = (["top", "center", "bottom"] as const).flatMap(
  (vertical) =>
    (["left", "center", "right"] as const).map((horizontal) => ({
      label: `${{ top: "Top", center: "Mid", bottom: "Bottom" }[vertical]} ${
        vertical === "center"
          ? { left: "Left", center: "Mid", right: "Right" }[horizontal]
          : { left: "Left", center: "Center", right: "Right" }[horizontal]
      }`,
      placement: { horizontal, vertical },
    })),
);

const FREQUENCIES: readonly { value: Frequency; label: string }[] = [
  { value: "punctuation_or_pause", label: "Punctuation or pause" },
  { value: "single_word", label: "Single word" },
  { value: "time", label: "Time" },
  { value: "random", label: "Random" },
];

const ANIMATION_CHOICES = [
  { value: "none", label: "None" },
  ...(Object.keys(CAPTION_ANIMATIONS) as CaptionAnimationPreset[]).map((preset) => ({
    value: preset,
    label: CAPTION_ANIMATIONS[preset].label,
  })),
];

const SCOPES: readonly { value: CaptionScope; label: string }[] = [
  { value: "page", label: "Page" },
  { value: "line", label: "Line" },
  { value: "word", label: "Word" },
];

/** A decimal of the theme editor, committed on Enter or blur, as live's number boxes. */
function DecimalField(props: {
  label: string;
  value: number;
  /** The contract's range: a number past it is taken at its end, so no save is refused for it. */
  min: number;
  max: number;
  step?: number;
  onCommit: (value: number) => void;
}) {
  const [revision, setRevision] = useState(0);
  const commit = (raw: string) => {
    const typed = Number(raw);
    const next = Math.min(props.max, Math.max(props.min, typed));
    if (raw.trim() !== "" && Number.isFinite(typed) && next !== props.value) props.onCommit(next);
    setRevision((current) => current + 1);
  };
  return (
    <NumberInput
      key={`${props.value}:${revision}`}
      aria-label={props.label}
      classNames={{ input: classes.themeInput }}
      defaultValue={props.value}
      decimalScale={2}
      step={props.step ?? 0.05}
      clampBehavior="none"
      hideControls
      onBlur={(event) => commit(event.currentTarget.value)}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") commit(event.currentTarget.value);
      }}
    />
  );
}

export function ThemeEditor(props: ThemeEditorProps) {
  const [tab, setTab] = useState<ThemeTab>("captions");
  const settings = props.timeline.captions;
  const captions = props.timeline.clips
    .filter((clip): clip is CaptionClip => clip.kind === "caption")
    .sort((one, other) => one.startMs - other.startMs);
  const shown = captions.find((caption) => caption.id === props.chosenCaption) ?? captions[0];
  // The timeline as it is now, for a re-cut that resumes after the face loads.
  const current = useRef(props.timeline);
  useEffect(() => {
    current.current = props.timeline;
  });
  // The latest re-cut asked for: an earlier one that resolves later is dropped, its change kept
  // in `pending` for the latest to carry, so quick changes to two fields both land.
  const asked = useRef(0);
  const pending = useRef<Partial<Paragraph>>({});
  /** The look on every caption. */
  const relook = (next: CaptionSettings) =>
    props.onEdit({ type: "setCaptions", captions: next, cut: null, idPrefix: newId(), newTrackId: newId() });
  const restyle = (patch: Partial<CaptionStyle>) => relook({ ...settings, style: { ...settings.style, ...patch } });
  /**
   * Every caption cut again (live's Paragraph changes, each one field): the face measured first,
   * then the timeline cuts its captions as they are by then, with the look as it is by then and
   * the fields changed. Refused, saying so, while a caption is locked or past 1000 captions.
   */
  const recut = async (patch: Partial<Paragraph>) => {
    const refused = recutRefusal(props.timeline);
    if (refused !== null) {
      props.onSay(refused);
      return;
    }
    const request = ++asked.current;
    const change = { ...pending.current, ...patch };
    pending.current = change;
    const words = spokenWords(props.timeline);
    const cut = await captionCut({ ...settings, paragraph: { ...settings.paragraph, ...change } }, words, props.aspectRatio);
    if (request !== asked.current) return;
    pending.current = {};
    const now = current.current;
    const refusedNow = recutRefusal(now);
    if (refusedNow !== null) {
      props.onSay(refusedNow);
      return;
    }
    const next = { ...now.captions, paragraph: { ...now.captions.paragraph, ...change } };
    const count = cutWith(spokenWords(now), next, cut).length;
    if (count > MAX_CAPTIONS) {
      props.onSay({ tone: "error", text: `These settings would make ${count} captions; a video holds ${MAX_CAPTIONS}.` });
      return;
    }
    props.onEdit({ type: "setCaptions", captions: next, cut, idPrefix: newId(), newTrackId: newId() });
  };
  /** Every unlocked caption's box changed, its words and place kept. */
  const reboxed = (next: CaptionSettings, box: (caption: CaptionClip) => CaptionBox) =>
    props.onEdit({ type: "setCaptions", captions: next, cut: null, idPrefix: newId(), newTrackId: newId(), boxes: box });
  const body: Record<ThemeTab, ReactNode> = {
    captions: (
      <CaptionsSection
        teamId={props.teamId}
        readOnly={props.readOnly}
        onSay={props.onSay}
        settings={settings}
        shown={shown}
        aspectRatio={props.aspectRatio}
        restyle={restyle}
        relook={relook}
        recut={recut}
        reboxed={reboxed}
      />
    ),
    paragraph: <ParagraphSection paragraph={settings.paragraph} onChange={(patch) => void recut(patch)} />,
    active: <ActiveSection style={settings.style} restyle={restyle} />,
    emphasize: <EmphasizeSection settings={settings} restyle={restyle} relook={relook} />,
  };
  return (
    <div className={classes.theme}>
      <div className={classes.tabs} role="tablist" aria-label="Caption properties">
        {TABS.map((each) => (
          <button
            key={each.id}
            type="button"
            role="tab"
            aria-selected={tab === each.id}
            data-active={tab === each.id}
            className={classes.tabButton}
            onClick={() => setTab(each.id)}
          >
            {each.label}
          </button>
        ))}
      </div>
      <fieldset className={classes.themeFields} disabled={props.readOnly}>
        {body[tab]}
      </fieldset>
    </div>
  );
}

/** Live's Captions tab: Typography, spacing, Text Color, Stroke, Shadow, Text Background, Size and
 * position, Transitions, and Save style as preset. */
function CaptionsSection(props: {
  teamId: string;
  /** Also the theme's fieldset's; Save says it itself, as it reads no field. */
  readOnly: boolean;
  onSay: (said: Said | null) => void;
  settings: CaptionSettings;
  shown: CaptionClip | undefined;
  aspectRatio: AspectRatio;
  restyle: (patch: Partial<CaptionStyle>) => void;
  relook: (next: CaptionSettings) => void;
  recut: (patch: Partial<Paragraph>) => Promise<void>;
  reboxed: (next: CaptionSettings, box: (caption: CaptionClip) => CaptionBox) => void;
}) {
  const { settings, restyle } = props;
  const style = settings.style;
  const styles = stylesOf(style.font);
  const current = styles.find((each) => each.weight === style.weight && each.italic === style.italic);
  const frame = liveFrame(props.aspectRatio);
  const box = props.shown?.box;
  const position = POSITIONS.find(
    (each) => each.placement.horizontal === settings.placement.horizontal && each.placement.vertical === settings.placement.vertical,
  );
  const animation = settings.animation;
  return (
    <>
      <ParamSection title="Typography">
        <Select
          aria-label="Font"
          placeholder="Search fonts..."
          classNames={{ input: classes.themeInput }}
          searchable
          nothingFoundMessage="No fonts found"
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          data={FONT_FAMILIES.map((family) => ({ value: family, label: family }))}
          value={style.font}
          onChange={(family) => {
            if (family === null) return;
            const kept = stylesOf(family as FontFamily);
            const near = kept.find((each) => each.weight === style.weight && each.italic === style.italic) ?? kept[0];
            restyle({ font: family as FontFamily, weight: near?.weight ?? style.weight, italic: near?.italic ?? false });
          }}
        />
        <LookRow label="Style">
          <Select
            aria-label="Style"
            placeholder="Style"
            classNames={{ input: classes.themeInput }}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            data={styles.map((each) => ({ value: each.label, label: each.label }))}
            value={current?.label ?? null}
            onChange={(label) => {
              const chosen = styles.find((each) => each.label === label);
              if (chosen !== undefined) restyle({ weight: chosen.weight, italic: chosen.italic });
            }}
          />
          <LookNumber
            label="Size"
            tag="Aa"
            value={style.size}
            min={captionStyleSizeMin}
            max={captionStyleSizeMax}
            onCommit={(size) => restyle({ size })}
          />
        </LookRow>
        <LookRow label="Align">
          {ALIGNS.map((each) => (
            <Toggle key={each.value} label={each.label} on={style.align === each.value} onClick={() => restyle({ align: each.value })}>
              {each.icon}
            </Toggle>
          ))}
        </LookRow>
        <LookRow label="Decoration">
          <Toggle label="Underline" on={style.underline} onClick={() => restyle({ underline: !style.underline })}>
            <Underline size={14} />
          </Toggle>
          <Toggle label="Overline" on={style.overline} onClick={() => restyle({ overline: !style.overline })}>
            <span className={classes.mark}>O</span>
          </Toggle>
          <Toggle label="Strikethrough" on={style.strikethrough} onClick={() => restyle({ strikethrough: !style.strikethrough })}>
            <Strikethrough size={14} />
          </Toggle>
        </LookRow>
        <LookRow label="Case">
          {CASES.map((each) => (
            <Toggle key={each.value} label={each.label} on={style.textCase === each.value} onClick={() => restyle({ textCase: each.value })}>
              {each.icon}
            </Toggle>
          ))}
        </LookRow>
        <LookRow label="Letter spacing">
          <DecimalField
            label="Letter spacing"
            value={style.letterSpacing}
            min={captionStyleLetterSpacingMin}
            max={captionStyleLetterSpacingMax}
            step={0.1}
            onCommit={(letterSpacing) => restyle({ letterSpacing })}
          />
        </LookRow>
        <LookRow label="Line spacing">
          {/* Live's renderer's own factor: 1.15 of the size by default (spec decision 26). */}
          <DecimalField
            label="Line spacing"
            value={Math.round(style.lineHeight * LINE_PITCH * 100) / 100}
            min={captionStyleLineHeightMin * LINE_PITCH}
            max={captionStyleLineHeightMax * LINE_PITCH}
            step={0.1}
            onCommit={(spacing) => restyle({ lineHeight: spacing / LINE_PITCH })}
          />
        </LookRow>
      </ParamSection>
      <ExtraSection title="Text Color" open={style.color !== null} onAdd={() => restyle({ color: ADDED_COLOR })} onRemove={() => restyle({ color: null })}>
        {style.color === null ? null : (
          <LookRow label="Color">
            <ColorField label="Text color" value={style.color} onCommit={(color) => restyle({ color })} />
          </LookRow>
        )}
      </ExtraSection>
      <ExtraSection title="Stroke" open={style.stroke !== null} onAdd={() => restyle({ stroke: ADDED_STROKE })} onRemove={() => restyle({ stroke: null })}>
        {style.stroke === null ? null : <StrokeFields stroke={style.stroke} onChange={(stroke) => restyle({ stroke })} />}
      </ExtraSection>
      <ExtraSection title="Shadow" open={style.shadow !== null} onAdd={() => restyle({ shadow: ADDED_SHADOW })} onRemove={() => restyle({ shadow: null })}>
        {style.shadow === null ? null : (
          // An edit keeps the shadow's alpha (spec decision 29: live's set it to 0.85).
          <ShadowFields shadow={style.shadow} onChange={(next) => restyle({ shadow: { ...style.shadow!, ...next } })} />
        )}
      </ExtraSection>
      <ExtraSection
        title="Text Background"
        open={style.background !== null}
        onAdd={() => restyle({ background: { color: style.color ?? "#000000", opacity: 1, radius: 4, padX: 8, padY: 4 } })} // raw-color: live's default
        onRemove={() => restyle({ background: null })}
      >
        {style.background === null ? null : (
          <BackgroundFields background={style.background} onChange={(background) => restyle({ background })} />
        )}
      </ExtraSection>
      <ParamSection title="Size and position">
        <Select
          aria-label="Position"
          placeholder="Position"
          classNames={{ input: classes.themeInput }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          data={POSITIONS.map((each) => each.label)}
          value={position?.label ?? null}
          onChange={(label) => {
            const chosen = POSITIONS.find((each) => each.label === label);
            if (chosen === undefined) return;
            // Every caption's box to its place by the position (live's), its size kept.
            props.reboxed({ ...settings, placement: chosen.placement }, (caption) =>
              placedBox(caption.box.width, caption.box.height, frame, chosen.placement),
            );
          }}
        />
        {box === undefined ? null : (
          <>
            <LookRow label="Position">
              <LookNumber
                label="X"
                tag="X"
                value={box.x}
                min={boxXMin}
                max={boxXMax}
                onCommit={(x) => props.reboxed(settings, (caption) => ({ ...caption.box, x: caption.box.x + (x - box.x) }))}
              />
              <LookNumber
                label="Y"
                tag="Y"
                value={box.y}
                min={boxYMin}
                max={boxYMax}
                onCommit={(y) => props.reboxed(settings, (caption) => ({ ...caption.box, y: caption.box.y + (y - box.y) }))}
              />
            </LookRow>
            <LookRow label="Size">
              <LookNumber label="W" tag="W" value={box.width} min={1} max={boxWidthMax} onCommit={(width) => props.reboxed(settings, (caption) => ({ ...caption.box, width }))} />
              <LookNumber label="H" tag="H" value={box.height} min={1} max={boxHeightMax} onCommit={(height) => props.reboxed(settings, (caption) => ({ ...caption.box, height }))} />
            </LookRow>
          </>
        )}
        <Switch
          size="xs"
          label="Auto size fit lines"
          checked={settings.paragraph.autofit}
          onChange={(event) => void props.recut({ autofit: event.currentTarget.checked })}
        />
      </ParamSection>
      <ParamSection title="Transitions">
        <Select
          aria-label="Animation"
          placeholder="Select animation"
          classNames={{ input: classes.themeInput }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          data={ANIMATION_CHOICES}
          // A preset's own animation is none of the list's (spec decision 30: live's read "Opacity").
          value={animation === null ? "none" : animation.preset}
          onChange={(value) => {
            if (value === null) return;
            props.relook({
              ...settings,
              animation: value === "none" ? null : captionAnimationOf(value as CaptionAnimationPreset, animation?.scope ?? "word"),
            });
          }}
        />
        <div className={classes.scopes}>
          {SCOPES.map((each) => (
            <button
              key={each.value}
              type="button"
              className={classes.scope}
              aria-label={`${each.label} scope`}
              aria-pressed={(animation?.scope ?? "word") === each.value}
              onClick={() => {
                if (animation === null) return;
                const rebuilt =
                  animation.preset === null ? { ...animation, scope: each.value } : captionAnimationOf(animation.preset, each.value);
                props.relook({ ...settings, animation: rebuilt });
              }}
            >
              {each.label}
            </button>
          ))}
        </div>
      </ParamSection>
      <SaveAsPreset teamId={props.teamId} settings={settings} readOnly={props.readOnly} onSay={props.onSay} />
    </>
  );
}

/** Live's Paragraph tab: "Line breaks", each change cutting every caption again. A change sends
 * its one field, so quick changes to two fields both land. */
function ParagraphSection(props: { paragraph: Paragraph; onChange: (patch: Partial<Paragraph>) => void }) {
  const { paragraph, onChange } = props;
  return (
    <ParamSection title="Line breaks">
      <LookRow label="Quantity">
        <Select
          aria-label="Quantity"
          classNames={{ input: classes.themeInput }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          data={["1", "2", "3", "4", "5", "6"]}
          value={String(paragraph.quantity)}
          onChange={(value) => {
            if (value !== null) onChange({ quantity: Number(value) });
          }}
        />
      </LookRow>
      <LookRow label="Frequency">
        <Select
          aria-label="Frequency"
          classNames={{ input: classes.themeInput }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          data={FREQUENCIES.map((each) => ({ value: each.value, label: each.label }))}
          value={paragraph.frequency}
          onChange={(value) => {
            if (value !== null) onChange({ frequency: value as Frequency });
          }}
        />
      </LookRow>
      {paragraph.frequency === "punctuation_or_pause" ? (
        <>
          <LookRow label="Pause seconds">
            <DecimalField
              label="Pause seconds"
              value={paragraph.pauseS}
              min={captionParagraphPauseSMin}
              max={captionParagraphPauseSMax}
              onCommit={(pauseS) => onChange({ pauseS })}
            />
          </LookRow>
          <LookRow label="Character count">
            <LookNumber
              label="Character count"
              tag="#"
              value={paragraph.characterCount}
              min={1}
              max={captionParagraphCharacterCountMax}
              onCommit={(characterCount) => onChange({ characterCount })}
            />
          </LookRow>
        </>
      ) : null}
      {paragraph.frequency === "single_word" ? (
        <LookRow label="Words per line">
          <LookNumber
            label="Words per line"
            tag="#"
            value={paragraph.wordsPerLine}
            min={1}
            max={captionParagraphWordsPerLineMax}
            onCommit={(wordsPerLine) => onChange({ wordsPerLine })}
          />
        </LookRow>
      ) : null}
      {paragraph.frequency === "time" ? (
        <LookRow label="Split seconds">
          <DecimalField
            label="Split seconds"
            value={paragraph.splitS}
            min={captionParagraphSplitSMin}
            max={captionParagraphSplitSMax}
            step={0.5}
            onCommit={(splitS) => onChange({ splitS })}
          />
        </LookRow>
      ) : null}
    </ParamSection>
  );
}

/** Live's Active tab: the word being spoken's colour, the words to come, and the spoken word's box. */
function ActiveSection(props: { style: CaptionStyle; restyle: (patch: Partial<CaptionStyle>) => void }) {
  const { style, restyle } = props;
  const colour = (title: string, label: string, value: string | null, added: string, set: (next: string | null) => void) => (
    <ExtraSection title={title} open={value !== null} onAdd={() => set(added)} onRemove={() => set(null)}>
      {value === null ? null : (
        <LookRow label="Color">
          <ColorField label={label} value={value} onCommit={set} />
        </LookRow>
      )}
    </ExtraSection>
  );
  return (
    <>
      {colour("Active word color", "Active word color", style.activeColor, ADDED_ACTIVE_COLOR, (activeColor) => restyle({ activeColor }))}
      {colour("Upcoming words", "Upcoming words color", style.upcomingColor, ADDED_UPCOMING_COLOR, (upcomingColor) => restyle({ upcomingColor }))}
      {colour("Active word background", "Active word background", style.activeBackground, ADDED_ACTIVE_BACKGROUND, (activeBackground) =>
        restyle({ activeBackground }),
      )}
    </>
  );
}

/** Live's Emphasize tab: Positive Highlight, Negative / Tension, and Super size no break. */
function EmphasizeSection(props: {
  settings: CaptionSettings;
  restyle: (patch: Partial<CaptionStyle>) => void;
  relook: (next: CaptionSettings) => void;
}) {
  const { settings, restyle } = props;
  return (
    <>
      <TagSection title="Positive Highlight" tag={settings.style.positive} added={ADDED_TAG.positive} base={settings.style} onChange={(positive) => restyle({ positive })} />
      <TagSection title="Negative / Tension" tag={settings.style.negative} added={ADDED_TAG.negative} base={settings.style} onChange={(negative) => restyle({ negative })} />
      <div className={classes.superSize}>
        <div>
          <Text className={classes.superSizeTitle}>Super size no break</Text>
          <Text className={classes.superSizeText}>Expands bounding box instead of wrapping</Text>
        </div>
        <Switch
          size="xs"
          aria-label="Super size no break"
          checked={settings.superSizeNoBreak}
          onChange={(event) => props.relook({ ...settings, superSizeNoBreak: event.currentTarget.checked })}
        />
      </div>
    </>
  );
}

/** One of live's emphasis looks: "+" adds it, "−" takes it away; its face, colour, toggles and
 * motion. A change keeps the rest of the tag (spec decision 31: live's font pick dropped it). */
function TagSection(props: {
  title: string;
  tag: CaptionTag | null;
  added: CaptionTag;
  base: CaptionStyle;
  onChange: (tag: CaptionTag | null) => void;
}) {
  const { tag, onChange } = props;
  const set = (patch: Partial<CaptionTag>) => {
    if (tag !== null) onChange({ ...tag, ...patch });
  };
  const family = tag?.font ?? props.base.font;
  const styles = stylesOf(family);
  const current = styles.find((each) => each.weight === tag?.weight && each.italic === tag.italic);
  return (
    <ExtraSection title={props.title} open={tag !== null} onAdd={() => onChange(props.added)} onRemove={() => onChange(null)}>
      {tag === null ? null : (
        <>
          <Select
            aria-label={`${props.title} font`}
            placeholder="Search fonts..."
            classNames={{ input: classes.themeInput }}
            data-dim={tag.font === null || undefined}
            searchable
            nothingFoundMessage="No fonts found"
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            data={FONT_FAMILIES.map((each) => ({ value: each, label: each }))}
            value={family}
            onChange={(value) => {
              if (value !== null) set({ font: value as FontFamily });
            }}
          />
          <LookRow label="Style">
            <Select
              aria-label={`${props.title} style`}
              placeholder="Style"
              classNames={{ input: classes.themeInput }}
              disabled={tag.font === null}
              allowDeselect={false}
              comboboxProps={{ withinPortal: false }}
              data={styles.map((each) => ({ value: each.label, label: each.label }))}
              value={current?.label ?? null}
              onChange={(label) => {
                const chosen = styles.find((each) => each.label === label);
                if (chosen !== undefined) set({ weight: chosen.weight, italic: chosen.italic });
              }}
            />
          </LookRow>
          <LookRow label="Color">
            <ColorField label={`${props.title} color`} value={tag.color} onCommit={(color) => set({ color })} />
          </LookRow>
          <div className={classes.tagToggles}>
            <Toggle label="Custom Font Family" on={tag.font !== null} onClick={() => set({ font: tag.font === null ? props.base.font : null })}>
              <span className={classes.mark}>Aa</span>
            </Toggle>
            <Toggle label="Underline" on={tag.underline} onClick={() => set({ underline: !tag.underline })}>
              <Underline size={14} />
            </Toggle>
            <Toggle label="Supersize (120%)" on={tag.sizeRatio > 1} onClick={() => set({ sizeRatio: tag.sizeRatio > 1 ? 1 : SUPERSIZE })}>
              <span className={classes.mark}>A+</span>
            </Toggle>
            <Toggle label="Difference Filter" on={tag.difference} onClick={() => set({ difference: !tag.difference })}>
              <span className={classes.mark}>◐</span>
            </Toggle>
          </div>
          <Select
            aria-label={`${props.title} motion animation`}
            placeholder="Motion animation"
            classNames={{ input: classes.themeInput }}
            leftSection={<Loop size={14} aria-hidden />}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            data={ANIMATION_CHOICES}
            value={tag.animation === null ? "none" : tag.animation.preset}
            onChange={(value) => {
              if (value === null) return;
              set({ animation: value === "none" ? null : captionAnimationOf(value as CaptionAnimationPreset, "word") });
            }}
          />
        </>
      )}
    </ExtraSection>
  );
}

/**
 * Live's name for a saved look: the preset it was picked from, "(Custom)" once changed from it
 * ("Default" for live's own default look). Live looked the preset up in an old list and called
 * nearly every one "Hustle v3 (Custom)" (spec decision 34). The name fits the contract's 100
 * characters, and a saved "(Custom)" preset changed again keeps one "(Custom)".
 */
export function presetName(settings: CaptionSettings, presets: readonly CaptionPreset[]): string {
  const base = presets.find((preset) => preset.id === settings.presetId);
  if (base === undefined) return settings.presetId === null && sameLook(settings, DEFAULT_CAPTIONS) ? "Default" : `Default${CUSTOM}`;
  return sameLook(settings, presetLook(base, settings)) ? base.name : customName(base.name);
}

const CUSTOM = " (Custom)";

/** A changed preset's name: its base's, cut to leave room for one "(Custom)" in 100 characters
 * (the server counts code points, as `Array.from` does). */
function customName(base: string): string {
  const stem = base.endsWith(CUSTOM) ? base.slice(0, -CUSTOM.length) : base;
  const room = captionPresetCreateNameMax - CUSTOM.length;
  return `${Array.from(stem).slice(0, room).join("").trimEnd()}${CUSTOM}`;
}

function sameLook(one: CaptionSettings, other: CaptionSettings): boolean {
  const plain = (look: CaptionSettings) => JSON.stringify(toWireCaptions({ ...look, presetId: null }));
  return plain(one) === plain(other);
}

/** Live's "Save style as preset": the whole look kept for its maker, the project unchanged. */
function SaveAsPreset(props: {
  teamId: string;
  settings: CaptionSettings;
  readOnly: boolean;
  onSay: (said: Said | null) => void;
}) {
  const presets = useListCaptionPresets(props.teamId);
  const queryClient = useQueryClient();
  const save = useCreateCaptionPreset({
    mutation: {
      onSuccess: async () => {
        props.onSay({ tone: "success", text: "Saved style preset!" });
        await queryClient.invalidateQueries({ queryKey: getListCaptionPresetsQueryKey(props.teamId) });
      },
      onError: (error) => props.onSay({ tone: "error", text: `Failed to save preset: ${toUserMessage(error)}` }),
    },
  });
  // The name needs the presets: a list that failed to load is asked for again, and a second
  // failure is said, never a button left silently off.
  const onSave = async () => {
    const listed = presets.data === undefined ? await presets.refetch() : presets;
    if (listed.data === undefined) {
      props.onSay({ tone: "error", text: `Failed to save preset: ${toUserMessage(listed.error)}` });
      return;
    }
    save.mutate({
      teamId: props.teamId,
      data: { name: presetName(props.settings, listed.data), settings: toWireCaptions(props.settings) },
    });
  };
  return (
    <Button
      variant="default"
      fullWidth
      leftSection={<Bookmark size={14} aria-hidden />}
      loading={save.isPending}
      disabled={props.readOnly || save.isPending || presets.isPending}
      onClick={() => void onSave()}
    >
      {save.isPending ? "Saving preset..." : "Save style as preset"}
    </Button>
  );
}
